/**
 * Character card component for IA Corner displays.
 * Renders a card showing a character with background and image, with focus indicators
 * and lazy loading support for performance optimization.
 */

import Background from '@components/atoms/images/characterBackground';
import Card from '../../../../shared/types/props/card';
import Container from '../../atoms/container/characterItemContainer';
import Focus from '../../atoms/focus/characterCardListFocus';
import IACorner from '@shared/classes/iacorner';
import Image from '../../atoms/images/characterImage';
import MainContext from '../../../../shared/components/utils/context/mainContext';
import useList from '../../../../shared/components/utils/hooks/useList';
import { magicRemoteSelector } from '../../../../shared/store/slices/magicRemote.slice';
import { useAppSelector } from '../../../../shared/store/hooks';
import { useContext } from 'react';

const ITEMS_TO_DISPLAY = 20;

interface Props extends Omit<Card, 'item'> {
  item: IACorner;
}

/**
 * Character card component that displays IA Corner character information.
 * Handles lazy loading, focus states, and asset updates on hover.
 *
 * @param {Props} props - Component props including item, index, and onClick handler
 * @returns {JSX.Element | null} Character card element or null if not in view
 */
export default function CharacterCard({ item, index, onClick }: Props) {
  const { enabled } = useAppSelector(magicRemoteSelector);
  const { hidePreviousItem, setAsset } = useContext(MainContext);
  const { distanceToIndex, focused, fixed, listIndex } = useList(index);
  const { characterBackground, characterImage } = item || {};

  const displayCard = fixed || distanceToIndex < ITEMS_TO_DISPLAY;
  const displayFocus = !enabled && focused && fixed && listIndex === index;
  const displayImage = fixed || distanceToIndex > (hidePreviousItem ? -1 : -20);

  return displayCard ? (
    <Container
      backgroundColor={(item as IACorner)?.characterBackground}
      onClick={onClick}
      onMouseEnter={() => setAsset(item)}
      id='characterCardContainer'
    >
      {displayImage ? (
        <>
          <Background src={characterBackground} />
          <Image src={characterImage} />
        </>
      ) : null}
      {displayFocus ? <Focus id='characterCardFocus' /> : null}
    </Container>
  ) : null;
}
